// ./src/components/generic/RecordCard/RecordCard.tsx
//
// Generic card component for displaying expense-type records
// (checkpoints, expenses, refuels).
//
// COMPACT LAYOUT (v2):
// Desktop (sm+):
//   - Header row: [Checkbox] [TypeBadge] Date · Car · Odometer ... [Actions] [Price]
//   - Custom middle content (children)
//   - Footer row: MapPin location name · address | Weather | Tags — all horizontal
//
// Mobile (<sm):
//   - Same concept but stacked where needed
//   - Swipe actions for edit/delete
//
// Key changes from v1:
//   - Type indicator moved INTO the header row as a small badge (no left column)
//   - Location name + address combined on one line with " · " separator
//   - Weather shown inline with location on the same row
//   - Tighter vertical gaps (xs instead of sm/md)
//   - Overall much more compact vertically

import React, { useMemo, useCallback, useRef } from "react";
import {
  Paper,
  Stack,
  Group,
  Text,
  Checkbox,
  Box,
  ActionIcon,
  Tooltip,
  Skeleton,
  Badge,
} from "@mantine/core";
import { useTranslation } from "react-i18next";

import { formatDateTime } from "@/utils/dateHelpers";
import { formatCurrency } from "@/utils/formatHelpers";
import { hasWeatherData as checkHasWeatherData } from "@/utils/expenseHelpers";

import { ExpenseTag } from "@/utils/api/ExpenseTagService";

import { AppIcon } from "@/components/generic/AppIcon";
import { TagBadgeGroup } from "@/components/generic/TagBadge";
import { SwipeableCard, SwipeAction } from "@/components/generic/SwipeableCard";
import { WeatherDisplay, WeatherData } from "@/components/generic/WeatherDisplay";
import { OdometerDisplay } from "@/components/generic/OdometerDisplay";
import { AuditableRecord, AuditInfo } from "../AuditInfo";
import { useAppEnvironment } from "@/contexts/AppEnvironmentContext";

import classes from "./RecordCard.module.css";

// ============================================
// Types
// ============================================

export interface RecordCardProps<T> {
  /** The record data */
  record: T;

  /** Unique identifier for the record */
  recordId: string | undefined | null;

  // ============================================
  // Type Indicator — shown as inline badge in header
  // ============================================

  /** Icon name for the record type (e.g., "GasStation", "Receipt", "Flag") */
  typeIcon?: string;

  /** Color for the type indicator (e.g., "green", "blue", "orange") */
  typeColor?: string;

  /** Label for the record type (e.g., "Refuel", "Expense", "Checkpoint") */
  typeLabel?: string;

  // ============================================
  // Header Data
  // ============================================

  /** Date/time when the record occurred */
  whenDone?: string | null;

  // ============================================
  // Vehicle Data
  // ============================================

  /** Vehicle/car label */
  carLabel?: string | null;

  /** Odometer reading */
  odometer?: number | null;

  /** Distance unit label (e.g., "km", "mi") */
  distanceLabel: string;

  /** Whether this odometer reading has an anomaly */
  hasOdometerAnomaly?: boolean;

  // ============================================
  // Footer Data
  // ============================================

  /** Place name (e.g., gas station name, shop name) */
  whereDone?: string | null;

  /** Full location/address */
  location?: string | null;

  /** Weather data for the record */
  weatherData?: WeatherData | null;

  /** Tags associated with the record */
  tags?: ExpenseTag[] | null;

  // ============================================
  // Price Display (Optional)
  // ============================================

  /** Total price to display (if applicable) */
  totalPrice?: number | null;

  /** Currency code for the price */
  priceCurrency?: string | null;

  /** Fallback currency if priceCurrency is not set */
  fallbackCurrency?: string;

  /** Color for the price text */
  priceColor?: string;

  // ============================================
  // Selection & State
  // ============================================

  /** Whether the record is selected */
  selected: boolean;

  /** Callback when selection changes */
  onSelect: (id: string, checked: boolean) => void;

  /** Whether selection is enabled */
  selectable: boolean;

  /** Whether the card should be highlighted */
  highlighted: boolean;

  /** Whether a delete operation is in progress */
  isDeleting: boolean;

  // ============================================
  // Actions
  // ============================================

  /** Callback when the card is clicked */
  onClick?: (record: T) => void;

  /** Callback when edit action is triggered */
  onEdit?: (record: T) => void;

  /** Callback when duplicate action is triggered */
  onDuplicate?: (record: T) => void;

  /** Callback when delete action is triggered */
  onDelete?: (record: T) => void;

  // ============================================
  // Swipe Management (mobile only)
  // ============================================

  /** Force close the swipe actions */
  forceClose?: boolean;

  /** Callback when force close completes */
  onForceCloseComplete?: () => void;

  /** Callback when swipe state changes */
  onSwipeStateChange?: (isOpen: boolean) => void;

  // ============================================
  // Custom Content
  // ============================================

  /** Custom content to render in the middle section */
  children?: React.ReactNode;
}

// ============================================
// Skeleton Component
// ============================================

// Deterministic widths — avoid Math.random() which breaks reconciliation
const SKELETON_CONTENT_WIDTHS = [120, 160, 140, 180];

export interface RecordCardSkeletonProps {
  /** Whether to show checkbox skeleton */
  showCheckbox?: boolean;
  /** Whether to show price skeleton */
  showPrice?: boolean;
  /** Number of custom content skeleton rows */
  customContentRows?: number;
}

export const RecordCardSkeleton: React.FC<RecordCardSkeletonProps> = ({
  showCheckbox = true,
  showPrice = false,
  customContentRows = 2,
}) => {
  return (
    <Paper p="sm" withBorder>
      <Group gap="sm" wrap="nowrap" align="flex-start">
        {showCheckbox && (
          <Box pt={4}>
            <Skeleton height={20} width={20} radius="sm" />
          </Box>
        )}

        <Stack gap="xs" style={{ flex: 1 }}>
          {/* Header skeleton */}
          <Group gap="sm">
            <Skeleton height={22} width={60} radius="xl" />
            <Skeleton height={18} width={150} />
            <Skeleton height={18} width={80} />
          </Group>

          {/* Content rows */}
          {Array.from({ length: customContentRows }).map((_, index) => (
            <Group gap="md" key={index}>
              <Skeleton height={18} width={18} radius="sm" />
              <Skeleton
                height={18}
                width={SKELETON_CONTENT_WIDTHS[index % SKELETON_CONTENT_WIDTHS.length]}
              />
            </Group>
          ))}

          {/* Footer skeleton */}
          <Group gap="sm">
            <Skeleton height={18} width={18} radius="sm" />
            <Skeleton height={18} width={200} />
            <Skeleton height={18} width={80} />
          </Group>

          {showPrice && (
            <Group justify="flex-end">
              <Skeleton height={24} width={90} />
            </Group>
          )}
        </Stack>
      </Group>
    </Paper>
  );
};

// ============================================
// Main Component
// ============================================

function RecordCardComponent<T>({
  record,
  recordId,
  typeIcon,
  typeColor = "gray",
  typeLabel,
  whenDone,
  carLabel,
  odometer,
  distanceLabel,
  hasOdometerAnomaly = false,
  whereDone,
  location,
  weatherData,
  tags,
  totalPrice,
  priceCurrency,
  fallbackCurrency = "USD",
  priceColor = "green.7",
  selected,
  onSelect,
  selectable,
  highlighted,
  isDeleting,
  onClick,
  onEdit,
  onDuplicate,
  onDelete,
  forceClose,
  onForceCloseComplete,
  onSwipeStateChange,
  children,
}: RecordCardProps<T>) {
  const { t } = useTranslation(["common"]);
  const { isMobile } = useAppEnvironment();

  // ============================================
  // Callback ref mirrors
  // Callers may pass unstable (inline) functions. We store them in refs so
  // our own useCallback wrappers never need to re-create just because the
  // parent re-renders — but always call the latest version.
  // ============================================

  const onClickRef = useRef(onClick);
  const onEditRef = useRef(onEdit);
  const onDuplicateRef = useRef(onDuplicate);
  const onDeleteRef = useRef(onDelete);
  const onSelectRef = useRef(onSelect);
  const onSwipeStateChangeRef = useRef(onSwipeStateChange);

  onClickRef.current = onClick;
  onEditRef.current = onEdit;
  onDuplicateRef.current = onDuplicate;
  onDeleteRef.current = onDelete;
  onSelectRef.current = onSelect;
  onSwipeStateChangeRef.current = onSwipeStateChange;

  // ============================================
  // Event Handlers — stable references regardless of parent stability
  // ============================================

  const handleCheckboxChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      event.stopPropagation();
      if (recordId) {
        onSelectRef.current(recordId, event.currentTarget.checked);
      }
    },
    [recordId]
  );

  const handleClick = useCallback(() => {
    onClickRef.current?.(record);
  }, [record]);

  const handleKeyDown = useCallback(
    (event: React.KeyboardEvent) => {
      if (event.target !== event.currentTarget) return;
      if (event.key === "Enter" || event.key === " ") {
        event.preventDefault();
        onClickRef.current?.(record);
      }
    },
    [record]
  );

  const handleSwipeStateChange = useCallback((isOpen: boolean) => {
    onSwipeStateChangeRef.current?.(isOpen);
  }, []);

  const handleEditClick = useCallback(
    (e: React.MouseEvent) => {
      e.stopPropagation();
      onEditRef.current?.(record);
    },
    [record]
  );

  const handleDuplicateClick = useCallback(
    (e: React.MouseEvent) => {
      e.stopPropagation();
      onDuplicateRef.current?.(record);
    },
    [record]
  );

  const handleDeleteClick = useCallback(
    (e: React.MouseEvent) => {
      e.stopPropagation();
      onDeleteRef.current?.(record);
    },
    [record]
  );

  // ============================================
  // Swipe Actions (mobile only)
  // ============================================

  const leftSwipeActions = useMemo((): SwipeAction[] => {
    const actions: SwipeAction[] = [];
    if (onEdit) {
      actions.push({
        key: "edit",
        icon: "Edit",
        label: t("common:buttons.edit"),
        color: "blue",
        onClick: () => onEditRef.current?.(record),
        disabled: isDeleting,
      });
    }
    if (onDuplicate) {
      actions.push({
        key: "duplicate",
        icon: "Copy",
        label: t("common:buttons.duplicate"),
        color: "teal",
        onClick: () => onDuplicateRef.current?.(record),
        disabled: isDeleting,
      });
    }
    if (onDelete) {
      actions.push({
        key: "delete",
        icon: "Trash",
        label: t("common:buttons.delete"),
        color: "red",
        onClick: () => onDeleteRef.current?.(record),
        loading: isDeleting,
        disabled: isDeleting,
      });
    }
    return actions;
    // Refs are stable — only rebuild when the set of available actions or
    // isDeleting changes, not when the callback implementations change.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [!!onEdit, !!onDuplicate, !!onDelete, isDeleting, t, record]);

  // ============================================
  // Computed Values
  // ============================================

  const hasTags = !!tags && tags.length > 0;
  const hasWhereDone = !!whereDone;
  const hasLocation = !!location;
  // checkHasWeatherData already handles null/undefined internally
  const hasWeather = !!weatherData && checkHasWeatherData(weatherData);
  const hasPrice =
    totalPrice !== undefined && totalPrice !== null && totalPrice !== 0;
  const hasOdometer = odometer !== undefined && odometer !== null;
  const hasActions = !!onEdit || !!onDuplicate || !!onDelete;
  const hasFooter = hasWhereDone || hasLocation || hasWeather || hasTags;

  // ============================================
  // Shared computed elements — built once, used in both layouts
  // ============================================

  /** Formatted date — memoized so formatDateTime isn't called twice */
  const formattedDate = useMemo(
    () => (whenDone ? formatDateTime(whenDone, "datetime") : null),
    [whenDone]
  );

  /** Type badge — inline in header row */
  const typeBadgeEl = useMemo(
    () =>
      typeIcon && typeLabel ? (
        <Badge
          variant="light"
          size="sm"
          color={typeColor}
          leftSection={<AppIcon iconName={typeIcon} size={12} />}
          style={{ flexShrink: 0 }}
        >
          {typeLabel}
        </Badge>
      ) : null,
    [typeIcon, typeLabel, typeColor]
  );

  /** Location row — shared between mobile and desktop */
  const locationRowEl = useMemo(
    () =>
      hasWhereDone || hasLocation ? (
        <Group gap={6} wrap="nowrap" align="flex-start" style={{ minWidth: 0 }}>
          <AppIcon
            iconName="MapPin"
            size={16}
            color="gray"
            style={{ flexShrink: 0, marginTop: 3 }}
          />
          <Text size="md" c="dimmed" style={{ minWidth: 0 }}>
            {hasWhereDone && hasLocation
              ? `${whereDone} · ${location}`
              : whereDone || location}
          </Text>
        </Group>
      ) : null,
    [hasWhereDone, hasLocation, whereDone, location]
  );

  /** Weather element — shared between mobile and desktop */
  const weatherEl = useMemo(
    () =>
      hasWeather && weatherData ? (
        <WeatherDisplay
          data={weatherData}
          variant="small"
          showFeelsLike={true}
          showWind={true}
        />
      ) : null,
    // weatherData object identity may change even when values are identical;
    // hasWeather guards the expensive render regardless.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [hasWeather]
  );

  /** Tags element — shared */
  const tagsEl = useMemo(
    () =>
      hasTags ? (
        <TagBadgeGroup
          tags={tags!}
          size="sm"
          variant="filled"
          maxVisible={3}
          gap={8}
        />
      ) : null,
    [hasTags, tags]
  );

  /** Audit info element — shared */
  const auditInfoEl = <AuditInfo record={record as AuditableRecord} />;

  /** Formatted price — shared */
  const priceEl = useMemo(
    () =>
      hasPrice ? (
        <Text fw={700} c={priceColor}>
          {formatCurrency(totalPrice, priceCurrency || fallbackCurrency)}
        </Text>
      ) : null,
    [hasPrice, totalPrice, priceCurrency, fallbackCurrency, priceColor]
  );

  /** Desktop hover actions */
  const hoverActionsEl = useMemo(
    () =>
      hasActions ? (
        <Group gap={4} wrap="nowrap" className={classes.actions}>
          {onEdit && (
            <Tooltip label={t("common:buttons.edit")} withArrow position="bottom">
              <ActionIcon
                variant="subtle"
                color="blue"
                size="md"
                onClick={handleEditClick}
                disabled={isDeleting}
                aria-label={t("common:buttons.edit")}
              >
                <AppIcon iconName="Edit" size={16} />
              </ActionIcon>
            </Tooltip>
          )}
          {onDuplicate && (
            <Tooltip label={t("common:buttons.duplicate")} withArrow position="bottom">
              <ActionIcon
                variant="subtle"
                color="teal"
                size="md"
                onClick={handleDuplicateClick}
                disabled={isDeleting}
                aria-label={t("common:buttons.duplicate")}
              >
                <AppIcon iconName="Copy" size={16} />
              </ActionIcon>
            </Tooltip>
          )}
          {onDelete && (
            <Tooltip label={t("common:buttons.delete")} withArrow position="bottom">
              <ActionIcon
                variant="subtle"
                color="red"
                size="md"
                onClick={handleDeleteClick}
                loading={isDeleting}
                disabled={isDeleting}
                aria-label={t("common:buttons.delete")}
              >
                <AppIcon iconName="Trash" size={16} />
              </ActionIcon>
            </Tooltip>
          )}
        </Group>
      ) : null,
    // handleEditClick etc. are stable (ref-based); isDeleting and action
    // presence drive the only meaningful changes here.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [hasActions, !!onEdit, !!onDuplicate, !!onDelete, isDeleting, t]
  );

  // ============================================
  // Shared sub-sections
  // ============================================

  /** Car + odometer row — identical on mobile and desktop */
  const carOdometerRow = (carLabel || hasOdometer) && (
    <Group gap="md" wrap="wrap">
      {carLabel && (
        <Group gap={6}>
          <AppIcon iconName="Car" size={16} color="gray" />
          <Text size="md" c="dimmed">
            {carLabel}
          </Text>
        </Group>
      )}
      {hasOdometer && (
        <Group gap={6}>
          <AppIcon iconName="Gauge" size={16} color="gray" />
          <OdometerDisplay
            value={odometer}
            distanceLabel={distanceLabel}
            hasAnomaly={hasOdometerAnomaly}
            size="md"
          />
        </Group>
      )}
    </Group>
  );

  // ============================================
  // Common paper props
  // ============================================

  const paperProps = {
    p: "sm" as const,
    withBorder: true,
    bg: highlighted ? "green.0" : undefined,
    opacity: isDeleting ? 0.5 : 1,
    style: { transition: "box-shadow 150ms ease, opacity 150ms ease" },
    onClick: handleClick,
    onKeyDown: handleKeyDown,
    tabIndex: onClick ? 0 : undefined,
    role: onClick ? ("button" as const) : undefined,
  };

  // ============================================
  // MOBILE LAYOUT
  // ============================================

  if (isMobile) {
    const mobileCard = (
      <Paper {...paperProps}>
        <Group gap="sm" wrap="nowrap" align="flex-start">
          {selectable && (
            <Box pt={3}>
              <Checkbox
                checked={selected}
                onChange={handleCheckboxChange}
                onClick={(e) => e.stopPropagation()}
                disabled={isDeleting}
                size="sm"
                aria-label={t("common:form.selectItem", { defaultValue: "Select item" })}
              />
            </Box>
          )}

          <Stack gap={8} style={{ flex: 1, minWidth: 0 }}>
            {/* Row 1: TypeBadge + Date */}
            <Group gap="xs" wrap="nowrap" align="center">
              {typeBadgeEl}
              {formattedDate && (
                <Text size="md" fw={600} truncate="end" style={{ minWidth: 0 }}>
                  {formattedDate}
                </Text>
              )}
            </Group>

            {/* Row 2: Car · Odometer */}
            {carOdometerRow}

            {/* Custom middle content */}
            {children}

            {/* Location */}
            {locationRowEl}

            {/* Weather */}
            {weatherEl}

            {/* Tags */}
            {tagsEl}

            {/* Audit info */}
            {auditInfoEl}

            {/* Price */}
            {hasPrice && (
              <Group justify="flex-end">
                <Text fw={700} size="lg" c={priceColor}>
                  {formatCurrency(totalPrice, priceCurrency || fallbackCurrency)}
                </Text>
              </Group>
            )}
          </Stack>
        </Group>
      </Paper>
    );

    if (leftSwipeActions.length === 0) {
      return mobileCard;
    }

    return (
      <SwipeableCard
        leftSwipeActions={leftSwipeActions}
        forceClose={forceClose}
        onForceCloseComplete={onForceCloseComplete}
        onSwipeStateChange={handleSwipeStateChange}
        disabled={isDeleting}
        borderRadius="var(--mantine-radius-default)"
      >
        {mobileCard}
      </SwipeableCard>
    );
  }

  // ============================================
  // DESKTOP LAYOUT
  // ============================================

  return (
    <Paper {...paperProps} className={classes.card}>
      <Group gap="sm" wrap="nowrap" align="flex-start">
        {selectable && (
          <Box pt={4}>
            <Checkbox
              checked={selected}
              onChange={handleCheckboxChange}
              onClick={(e) => e.stopPropagation()}
              disabled={isDeleting}
              size="md"
              aria-label={t("common:form.selectItem", { defaultValue: "Select item" })}
            />
          </Box>
        )}

        <Stack gap="md" style={{ flex: 1, minWidth: 0 }}>
          {/* Row 1: TypeBadge · Date · Car · Odometer ... [Actions] [Price] */}
          <Group justify="space-between" wrap="nowrap">
            <Group gap="md" wrap="wrap" align="center" style={{ flex: 1 }}>
              {typeBadgeEl}

              {formattedDate && (
                <Text size="md" fw={600}>
                  {formattedDate}
                </Text>
              )}

              {carOdometerRow}
            </Group>

            {/* Right side: hover actions + price */}
            <Group gap="md" wrap="nowrap" style={{ flexShrink: 0 }}>
              {hoverActionsEl}
              {priceEl && (
                <Text fw={700} size="xl" c={priceColor}>
                  {formatCurrency(totalPrice, priceCurrency || fallbackCurrency)}
                </Text>
              )}
            </Group>
          </Group>

          {/* Custom middle content */}
          {children}

          {/* Footer: location · weather | tags */}
          {hasFooter && (
            <Group gap="lg" wrap="wrap" align="flex-start">
              {(locationRowEl || weatherEl) && (
                <Group gap="md" wrap="wrap" align="flex-start">
                  {locationRowEl}
                  {weatherEl}
                </Group>
              )}
              {tagsEl}
            </Group>
          )}

          {/* Audit info */}
          {auditInfoEl}
        </Stack>
      </Group>
    </Paper>
  );
}

export const RecordCard = React.memo(
  RecordCardComponent
) as typeof RecordCardComponent;